import React, { useState, useEffect, useRef } from 'react';
import { checkIn, checkOut, onMyAttendanceChange, hoursWorkedForDisplay, toDate } from '../services/attendanceDb';
import { MapPin, Clock, Calendar, LogIn, LogOut, Timer } from 'lucide-react';

const formatDuration = (hours) => {
  if (!hours || hours <= 0) return '--';
  const totalSeconds = Math.floor(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export default function MyAttendance({ currentUser, showToast }) {
  const [actionPending, setActionPending] = useState(false);
  const [history, setHistory] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const actionLock = useRef(false);

  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Real-time clock for calculating current hours
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Single listener — onSnapshot sends initial cache/server data (no extra getDoc)
  useEffect(() => {
    if (!currentUser?.email) return;

    const unsub = onMyAttendanceChange(currentUser.email, (data) => {
      setHistory(data);
      const todayData = data.find((h) => h.date === today);
      setTodayAttendance(todayData || null);
    });

    return unsub;
  }, [currentUser?.email, today]);

  const handleCheckIn = async () => {
    if (actionLock.current) return;
    actionLock.current = true;
    setActionPending(true);
    const optimistic = {
      checkIn: new Date().toISOString(),
      date: today,
      status: 'IN',
      optimistic: true,
    };
    setTodayAttendance(optimistic);
    try {
      const result = await checkIn(currentUser.email);
      showToast(result.message, 'ok');
    } catch (error) {
      setTodayAttendance(null);
      showToast(error.message || 'Failed to check in', 'err');
    } finally {
      actionLock.current = false;
      setActionPending(false);
    }
  };

  const handleCheckOut = async () => {
    if (actionLock.current || !todayAttendance) return;
    actionLock.current = true;
    setActionPending(true);
    const checkInTime = toDate(todayAttendance.checkIn);
    const checkoutDate = new Date();
    const totalHours = checkInTime
      ? (checkoutDate - checkInTime) / (1000 * 60 * 60)
      : 0;
    const optimistic = {
      ...todayAttendance,
      checkOut: checkoutDate.toISOString(),
      status: 'OUT',
      totalHours,
      optimistic: true,
    };
    setTodayAttendance(optimistic);
    try {
      const result = await checkOut(currentUser.email);
      showToast(result.message, 'ok');
    } catch (error) {
      showToast(error.message || 'Failed to check out', 'err');
    } finally {
      actionLock.current = false;
      setActionPending(false);
    }
  };
  
  const getCurrentWorkingHours = () => {
    if (!todayAttendance?.checkIn) return 0;
    return Math.round(
      hoursWorkedForDisplay(
        todayAttendance.checkIn,
        todayAttendance.checkOut,
        todayAttendance.date || today,
        todayAttendance.status
      ) * 100
    ) / 100;
  };

  return (
    <div className="pg">
      <div className="ph" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MapPin size={24} /> My Attendance
          </h1>
          <p style={{ color: 'var(--text2)', fontSize: '14px', marginTop: '4px' }}>
            Working Policy: 9 hours/day | Full Day: &gt;= 9 hours | Half Day: {"<"} 9 hours
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text)' }}>
            {currentTime.toLocaleTimeString()}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text2)' }}>
            {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
      </div>

      {/* Today's Status Card */}
      <div className="card" style={{ marginBottom: '24px', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
          {/* Status Display */}
          <div style={{ flex: 1, minWidth: '250px' }}>
            <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={18} /> Today's Status
            </h3>
            
            {!todayAttendance ? (
              <div style={{ 
                padding: '16px 20px', 
                background: 'var(--bg-sec)', 
                borderRadius: '8px',
                border: '1px dashed var(--border)'
              }}>
                <p style={{ color: 'var(--text2)', fontSize: '14px', margin: 0 }}>
                  You haven't checked in yet today.
                </p>
                <p style={{ color: 'var(--text3)', fontSize: '12px', margin: '8px 0 0 0' }}>
                  Click the Check In button to start your shift.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Check In */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  padding: '12px 16px',
                  background: '#edfaf2',
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '50%', 
                    background: '#2a9d4f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <LogIn size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text2)' }}>Check In</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#2a9d4f' }}>
                      {toDate(todayAttendance.checkIn)?.toLocaleTimeString() || '--'}
                    </div>
                  </div>
                </div>
                
                {/* Current Status or Check Out */}
                {todayAttendance.status === 'IN' ? (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    padding: '12px 16px',
                    background: '#fffbeb',
                    borderRadius: '8px',
                    border: '1px solid #fcd9a7'
                  }}>
                    <div style={{ 
                      width: '36px', 
                      height: '36px', 
                      borderRadius: '50%', 
                      background: '#d97706',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      <Timer size={18} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: 'var(--text2)' }}>Currently Working</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#d97706' }}>
                        {getCurrentWorkingHours().toFixed(2)} hrs
                      </div>
                    </div>
                    <span style={{ 
                      padding: '4px 10px', 
                      background: '#fffbeb', 
                      color: '#d97706',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      WORKING
                    </span>
                  </div>
                ) : todayAttendance.checkOut ? (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    padding: '12px 16px',
                    background: '#eef2ff',
                    borderRadius: '8px'
                  }}>
                    <div style={{ 
                      width: '36px', 
                      height: '36px', 
                      borderRadius: '50%', 
                      background: '#3b5bdb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      <LogOut size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--text2)' }}>Check Out</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b5bdb' }}>
                        {toDate(todayAttendance.checkOut)?.toLocaleTimeString() || '--'}
                      </div>
                    </div>
                  </div>
                ) : null}
                
                {/* Total Hours Summary */}
                {todayAttendance.status !== 'IN' && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: todayAttendance.totalHours >= 9 ? '#edfaf2' : '#fffbeb',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Clock size={16} color={todayAttendance.totalHours >= 9 ? '#2a9d4f' : '#d97706'} />
                      <span style={{ fontSize: '14px', color: 'var(--text2)' }}>Total Hours</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ 
                        fontSize: '18px', 
                        fontWeight: '700', 
                        color: todayAttendance.totalHours >= 9 ? '#2a9d4f' : '#d97706' 
                      }}>
                        {todayAttendance.totalHours.toFixed(2)} hrs
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Action Button */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            {!todayAttendance ? (
              <button 
                className="btn btn-p" 
                onClick={handleCheckIn} 
                disabled={actionPending} 
                style={{ 
                  padding: '16px 32px', 
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  borderRadius: '12px'
                }}
              >
                <LogIn size={20} /> Check In
              </button>
            ) : todayAttendance.status === 'IN' ? (
              <button 
                className="btn btn-ng" 
                onClick={handleCheckOut} 
                disabled={actionPending} 
                style={{ 
                  padding: '16px 32px', 
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  borderRadius: '12px'
                }}
              >
                <LogOut size={20} /> Check Out
              </button>
            ) : (
              <div style={{ 
                padding: '16px 32px', 
                background: '#edfaf2', 
                color: '#2a9d4f', 
                borderRadius: '12px', 
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                border: '2px solid #2a9d4f'
              }}>
                <Clock size={20} /> Shift Completed
              </div>
            )}
            
            <p style={{ fontSize: '12px', color: 'var(--text3)', textAlign: 'center', maxWidth: '200px' }}>
              {!todayAttendance ? 'Start your work day' : todayAttendance.status === 'IN' ? 'End your shift when done' : 'Great work today!'}
            </p>
          </div>
        </div>
      </div>

      {/* Attendance History */}
      <div className="card" style={{ padding: '0' }}>
        <div style={{ 
          padding: '16px 20px', 
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={18} /> Attendance History
          </h3>
          <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
            Last 30 days
          </span>
        </div>
        
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ minWidth: '600px', width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-sec)', textAlign: 'left' }}>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500' }}>DATE</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500' }}>CHECK IN</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500' }}>CHECK OUT</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500' }}>HOURS</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500' }}>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: 'var(--text2)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <Calendar size={32} style={{ color: 'var(--text3)' }} />
                    <span>No attendance history found</span>
                    <span style={{ fontSize: '12px', color: 'var(--text3)' }}>Your attendance records will appear here</span>
                  </div>
                </td>
              </tr>
            ) : history.map((h, index) => {
              const isToday = h.date === today;
              return (
                <tr key={h.date} style={{ 
                  borderBottom: '1px solid var(--border)',
                  background: isToday ? 'rgba(59, 91, 219, 0.05)' : 'transparent'
                }}>
                  <td style={{ padding: '12px 16px', fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {h.date}
                      {isToday && (
                        <span style={{ 
                          padding: '2px 8px', 
                          background: '#dbeafe', 
                          color: '#3b5bdb',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          TODAY
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text)' }}>
                    {toDate(h.checkIn)?.toLocaleTimeString() || '--'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text)' }}>
                    {h.checkOut ? 
                      (toDate(h.checkOut)?.toLocaleTimeString() || '--') 
                      : (h.status === 'IN' ? <span style={{ color: '#d97706' }}>In Progress...</span> : '--')}
                  </td>
                  <td style={{ padding: '12px 16px', fontWeight: '600', fontSize: '13px' }}>
                    {h.totalHours > 0 ? (
                      <span style={{ color: h.totalHours >= 9 ? '#2a9d4f' : '#d97706' }}>
                        {formatDuration(h.totalHours)}
                      </span>
                    ) : '--'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ 
                      padding: '4px 10px', 
                      borderRadius: '12px', 
                      fontSize: '11px', 
                      fontWeight: '600',
                      background: h.status === 'IN' ? '#fffbeb' : 
                                h.status === 'OUT' || h.status === 'AUTO_CHECKOUT' ? '#eef2ff' : '#fee2e2',
                      color: h.status === 'IN' ? '#d97706' : 
                             h.status === 'OUT' || h.status === 'AUTO_CHECKOUT' ? '#3b5bdb' : '#dc2626'
                    }}>
                      {h.status === 'IN' ? 'WORKING' : 
                       (h.status === 'OUT' || h.status === 'AUTO_CHECKOUT') ? 'OUT' : 'ABSENT'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        
        {history.length > 0 && (
          <div style={{ 
            padding: '12px 20px', 
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: 'var(--text3)'
          }}>
            <span>Showing {history.length} records</span>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 600px) {
          .ph { flex-direction: column !important; align-items: flex-start !important; }
          .ph div:last-child { text-align: left !important; margin-top: 10px; }
          .card { padding: 16px !important; }
        }
      `}</style>
    </div>
  );
}
